/**
 * PatientReviewTable
 *
 * Shared data table for displaying new patient review items.
 * Derives columns from the items in the current page.
 */

import { Fragment, useMemo, useState } from 'react';
import {
  Alert,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { TARGET_FIELD_LABELS } from '../../../types';
import type { PatientReviewTableProps } from '../ImportReviewWizard.types';

import { getClinicalCounts } from './clinicalCounts';
import { ClinicalReviewDetail, ClinicalSummaryToggle } from './ClinicalReviewDetail';
import { NEEDS_REVIEW_CONFIDENCE, PayerConfidenceChip } from './PayerConfidenceChip';
import { reviewTableSx } from './reviewTableStyles';

// =============================================================================
// Column Configuration
// =============================================================================

interface ColumnDef {
  key: string;
  label: string;
  minWidth: number;
}

/**
 * Keys from the raw API response that should never appear as table columns.
 * These are internal metadata or replaced by derived virtual columns below.
 */
const METADATA_KEYS = new Set([
  'extracted_patient_id',
  // UUID fields — shown via *_payer_status virtual columns instead
  'resolved_payer_uuid',
  'secondary_resolved_payer_uuid',
  'tertiary_resolved_payer_uuid',
  // Confidence fields — consumed to build *_payer_status virtual columns
  'resolved_payer_confidence',
  'secondary_resolved_payer_confidence',
  'tertiary_resolved_payer_confidence',
  // Clinical data — rendered in the expandable detail row, not as flat columns
  'diagnoses',
  'medications',
  'allergies',
]);

/**
 * Labels for fields not in TARGET_FIELD_LABELS (secondary/tertiary insurance,
 * virtual payer status columns, etc.)
 */
const SUPPLEMENTAL_FIELD_LABELS: Record<string, string> = {
  secondary_payer_name: 'Secondary Payer',
  secondary_payer_id: 'Secondary Payer ID',
  secondary_member_id: 'Secondary Member ID',
  secondary_group_number: 'Secondary Group / Policy #',
  secondary_relationship_to_insured: 'Secondary Relationship To Insured',
  tertiary_payer_name: 'Tertiary Payer',
  tertiary_payer_id: 'Tertiary Payer ID',
  tertiary_member_id: 'Tertiary Member ID',
  tertiary_group_number: 'Tertiary Group / Policy #',
  tertiary_relationship_to_insured: 'Tertiary Relationship To Insured',
  // Virtual payer status columns (one per coverage tier)
  payer_status: 'Payer Status',
  secondary_payer_status: 'Secondary Payer Status',
  tertiary_payer_status: 'Tertiary Payer Status',
};

/**
 * All insurance fields are grouped together at the right side of the table.
 * Primary → secondary → tertiary, each with payer status at the end of its cluster.
 */
const DEFERRED_ORDER = [
  // Primary insurance
  'payer_status',
  'payer_name',
  'payer_id',
  'member_id',
  'group_number',
  'relationship_to_insured',
  // Secondary insurance
  'secondary_payer_name',
  'secondary_payer_id',
  'secondary_member_id',
  'secondary_group_number',
  'secondary_relationship_to_insured',
  'secondary_payer_status',
  // Tertiary insurance
  'tertiary_payer_name',
  'tertiary_payer_id',
  'tertiary_member_id',
  'tertiary_group_number',
  'tertiary_relationship_to_insured',
  'tertiary_payer_status',
] as const;

const DEFERRED_KEYS = new Set<string>(DEFERRED_ORDER);


// =============================================================================
// Flattening helpers
// =============================================================================

/**
 * Flatten nested objects (e.g. `insurance: { payer_name, policy_number, … }`)
 * into top-level keys so each field gets its own column.
 * Primitive values are kept as-is; nested objects are spread and the parent key removed.
 */
function flattenPatient(patient: Record<string, unknown>): Record<string, string | null | undefined> {
  const result: Record<string, string | null | undefined> = {};
  for (const [key, value] of Object.entries(patient)) {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      for (const [nestedKey, nestedValue] of Object.entries(value as Record<string, unknown>)) {
        result[nestedKey] = nestedValue == null ? null : String(nestedValue);
      }
    } else {
      result[key] = value == null ? null : String(value);
    }
  }
  return result;
}

/**
 * Inject virtual `*_payer_status` columns derived from the backend's
 * `resolved_payer_confidence` / `*_resolved_payer_confidence` fields.
 * The raw UUID and confidence fields are excluded from display via METADATA_KEYS.
 *
 * Backward compat: if only a UUID is present (no confidence field), treat as 'high'.
 */
function injectPayerStatus(flat: Record<string, string | null | undefined>, raw: Record<string, unknown>): void {
  // Primary
  if ('resolved_payer_confidence' in raw) {
    flat['payer_status'] = (raw['resolved_payer_confidence'] as string | null) ?? null;
  } else if ('resolved_payer_uuid' in raw) {
    flat['payer_status'] = raw['resolved_payer_uuid'] != null ? 'high' : null;
  }

  // Secondary
  if ('secondary_resolved_payer_confidence' in raw) {
    flat['secondary_payer_status'] = (raw['secondary_resolved_payer_confidence'] as string | null) ?? null;
  } else if ('secondary_resolved_payer_uuid' in raw) {
    flat['secondary_payer_status'] = raw['secondary_resolved_payer_uuid'] != null ? 'high' : null;
  }

  // Tertiary
  if ('tertiary_resolved_payer_confidence' in raw) {
    flat['tertiary_payer_status'] = (raw['tertiary_resolved_payer_confidence'] as string | null) ?? null;
  } else if ('tertiary_resolved_payer_uuid' in raw) {
    flat['tertiary_payer_status'] = raw['tertiary_resolved_payer_uuid'] != null ? 'high' : null;
  }
}

function getMinWidthForField(field: string): number {
  switch (field) {
    case 'address':
    case 'email':
    case 'emergency_contact_name':
      return 180;
    case 'emergency_contact_relationship':
      return 200;
    case 'first_name':
    case 'last_name':
    case 'preferred_name':
    case 'payer_name':
    case 'secondary_payer_name':
    case 'tertiary_payer_name':
      return 140;
    case 'payer_status':
    case 'secondary_payer_status':
    case 'tertiary_payer_status':
      return 150;
    case 'dob':
    case 'gender':
    case 'sex':
    case 'pronouns':
    case 'race':
    case 'zip':
    case 'state':
      return 100;
    default:
      return 120;
  }
}

/** Format a snake_case key into a Title Case label */
function formatFieldLabel(key: string): string {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Use a known label if available, otherwise format the raw key */
function getLabelForField(key: string): string {
  return (TARGET_FIELD_LABELS as Record<string, string>)[key]
    ?? SUPPLEMENTAL_FIELD_LABELS[key]
    ?? formatFieldLabel(key);
}

// =============================================================================
// Sticky Column Styles
// =============================================================================

const stickyColumnSx = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  backgroundColor: 'background.paper',
} as const;

const stickyHeaderColumnSx = {
  ...stickyColumnSx,
  zIndex: 3,
} as const;

// =============================================================================
// Component
// =============================================================================

export const PatientReviewTable = ({ patients, rowOffset = 0 }: PatientReviewTableProps) => {
  // The API may return nested objects / extra fields that the generated NewPatientItem
  // type doesn't reflect. Cast to Record so flattenPatient can safely spread those fields.
  const flatPatients = useMemo(
    () => patients.map((p) => {
      const raw = p as unknown as Record<string, unknown>;
      const flat = flattenPatient(raw);
      injectPayerStatus(flat, raw);
      return flat;
    }),
    [patients],
  );

  // Count patients that need manual payer review (any tier low/unresolved)
  const needsReviewCount = useMemo(
    () => flatPatients.filter((p) =>
      NEEDS_REVIEW_CONFIDENCE.has(p['payer_status'] ?? '') ||
      NEEDS_REVIEW_CONFIDENCE.has(p['secondary_payer_status'] ?? '') ||
      NEEDS_REVIEW_CONFIDENCE.has(p['tertiary_payer_status'] ?? ''),
    ).length,
    [flatPatients],
  );

  const allColumns = useMemo<ColumnDef[]>(() => {
    if (flatPatients.length === 0) return [];

    const keysInOrder: string[] = [];
    const keysSeen = new Set<string>();

    for (const patient of flatPatients) {
      for (const key of Object.keys(patient)) {
        if (!METADATA_KEYS.has(key) && !keysSeen.has(key)) {
          keysSeen.add(key);
          keysInOrder.push(key);
        }
      }
    }

    // Promote name columns to the front
    const priorityKeys = ['identifier', 'first_name', 'last_name', 'preferred_name', 'middle_name', 'dob'];
    const promoted = priorityKeys.filter((k) => keysSeen.has(k));
    const promotedSet = new Set(promoted);

    // Middle: primary insurance + demographics (whatever the API returns, minus deferred)
    const middle = keysInOrder.filter((k) => !promotedSet.has(k) && !DEFERRED_KEYS.has(k));

    // Deferred: secondary/tertiary tiers + payer_status columns, in a fixed logical order
    const deferred = DEFERRED_ORDER.filter((k) => keysSeen.has(k));

    return [...promoted, ...middle, ...deferred].map((key) => ({
      key,
      label: getLabelForField(key),
      minWidth: getMinWidthForField(key),
    }));
  }, [flatPatients]);

  // Only surface the clinical column/expander when at least one patient in this
  // page actually has clinical data (e.g. CCDA imports). CSV imports stay clean.
  const hasAnyClinical = useMemo(
    () => patients.some((p) => getClinicalCounts(p).total > 0),
    [patients],
  );

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Leading (#) + optional Clinical Data column, used for empty/detail colSpans.
  const leadingColCount = 1 + (hasAnyClinical ? 1 : 0);

  return (
    <>
      {needsReviewCount > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {needsReviewCount} patient{needsReviewCount !== 1 ? 's' : ''} have low-confidence or unresolved payer matches — verify insurance before finalizing import.
        </Alert>
      )}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={reviewTableSx}
      >
        <Table size="small" stickyHeader sx={{ minWidth: 'max-content' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 56, ...stickyHeaderColumnSx }}>
                #
              </TableCell>
              {hasAnyClinical && (
                <TableCell sx={{ fontWeight: 600, minWidth: 170, whiteSpace: 'nowrap' }}>
                  Clinical Data
                </TableCell>
              )}
              {allColumns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    fontWeight: 600,
                    minWidth: col.minWidth,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {flatPatients.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={leadingColCount + allColumns.length}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No patients in this category.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              flatPatients.map((patient, idx) => {
                const raw = patients[idx];
                const counts = getClinicalCounts(raw);
                const id = patient.extracted_patient_id ?? String(idx);
                const isOpen = expandedIds.has(id);
                return (
                  <Fragment key={id}>
                    <TableRow hover>
                      <TableCell sx={stickyColumnSx}>{rowOffset + idx + 1}</TableCell>
                      {hasAnyClinical && (
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <ClinicalSummaryToggle
                            counts={counts}
                            isOpen={isOpen}
                            onToggle={() => toggleExpanded(id)}
                          />
                        </TableCell>
                      )}
                      {allColumns.map((col) => {
                        const value = patient[col.key];
                        if (col.key === 'payer_status' || col.key === 'secondary_payer_status' || col.key === 'tertiary_payer_status') {
                          return (
                            <TableCell key={col.key} sx={{ whiteSpace: 'nowrap' }}>
                              {value ? <PayerConfidenceChip confidence={value} /> : '\u2014'}
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={col.key} sx={{ whiteSpace: 'nowrap' }}>
                            {value ?? '\u2014'}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {hasAnyClinical && (
                      <TableRow>
                        <TableCell
                          colSpan={leadingColCount + allColumns.length}
                          sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}
                        >
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <ClinicalReviewDetail
                              diagnoses={raw.diagnoses}
                              medications={raw.medications}
                              allergies={raw.allergies}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
